import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { Tone } from './format';

/** Inline sentence token that opens a small picker. Used by the policy builder. */
/** `tone` colors a chosen value by its status (e.g. the policy decision). */
export function Token({ label, set, tone, className, children, ariaLabel }: { label: ReactNode; set: boolean; tone?: Tone; className?: string; children: (close: () => void) => ReactNode; ariaLabel: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <span ref={ref} className="token-wrap">
      <button type="button" className={['token', set ? 'set' : '', className ?? ''].filter(Boolean).join(' ')} data-tone={tone} aria-haspopup="dialog" aria-expanded={open} aria-controls={id} aria-label={ariaLabel} onClick={() => setOpen(!open)}>
        {label}
        <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>
      </button>
      {open && (
        <div className="popover token-popover" id={id} role="dialog" aria-label={ariaLabel}>
          {children(() => setOpen(false))}
        </div>
      )}
    </span>
  );
}

export function MultiPick<T extends string>({ options, value, onChange, searchable }: { options: Array<[T, string, string?]>; value: T[]; onChange: (v: T[]) => void; searchable?: boolean }) {
  const [q, setQ] = useState('');
  const shown = q ? options.filter(([, l, s]) => `${l} ${s ?? ''}`.toLowerCase().includes(q.toLowerCase())) : options;
  return (
    <div>
      {searchable && <input type="search" autoFocus placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search options" />}
      {shown.slice(0, 200).map(([v, l, s]) => (
        <label className="opt" key={v}>
          <input type="checkbox" checked={value.includes(v)} onChange={(e) => onChange(e.target.checked ? [...value, v] : value.filter((x) => x !== v))} />
          <span>{l}{s && <span className="opt-sub">{s}</span>}</span>
        </label>
      ))}
      {shown.length === 0 && <div className="muted opt-empty">No matches</div>}
    </div>
  );
}

export function SinglePick<T extends string>({ options, value, onChange, close }: { options: Array<[T, string, string?]>; value: T; onChange: (v: T) => void; close: () => void }) {
  return (
    <div role="radiogroup">
      {options.map(([v, l, s]) => (
        <label className="opt" key={v}>
          <input type="radio" checked={value === v} onChange={() => { onChange(v); close(); }} />
          <span>{l}{s && <span className="opt-sub">{s}</span>}</span>
        </label>
      ))}
    </div>
  );
}

export function summarize(values: string[], names: Map<string, string>, empty: string, max = 2) {
  if (values.length === 0) return empty;
  const n = values.map((v) => names.get(v) ?? 'unknown');
  return n.length <= max ? n.join(' or ') : `${n.slice(0, max).join(', ')} +${n.length - max}`;
}
